import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { To } from 'react-router-dom';

import {
  useGoBack as useGoBackOriginal,
  useNavigateWithTransition as useNavigateWithTransitionOriginal,
} from '@lemoncloud/react-page-transition';
import type {
  NavigateWithTransitionFn,
  PageTransitionConfig,
  PlatformType,
  TransitionNavigateOptions,
} from '@lemoncloud/react-page-transition';

import { isRunningInNativeApp } from '../utils/native-host';

const detectPlatform = (): PlatformType | undefined => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return undefined;
};

const isNativeApp = isRunningInNativeApp();
const pageTransitionConfig: PageTransitionConfig = { detectPlatform };

// Commit the route synchronously inside the View Transitions callback via
// flushSync. The scroll restoration runs in a layout effect, so a synchronous
// commit guarantees the new page is laid out AND its scroll position is set
// before the API captures the new snapshot — without it the snapshot races
// React's async commit. Paired with the single `.app-scroll` container (the
// document itself never scrolls), this keeps the platform slide flash-free on
// the iOS WebView: the old/new snapshots are captured at the on-screen scroll
// position instead of the document top.
const TRANSITION_OPTIONS: TransitionNavigateOptions = { legacyFlushSync: true };

/**
 * Navigate with a native-style page transition (iOS slide / Android lift)
 * when running inside the native shell; plain react-router navigation in a
 * browser. Drop-in replacement for `useNavigate` for intra-app navigation.
 */
export const useNavigateWithTransition = (): NavigateWithTransitionFn => {
  const plainNavigate = useNavigate();
  const transitionNavigate =
    useNavigateWithTransitionOriginal(pageTransitionConfig);

  const nativeNavigate = useCallback(
    (to: To | number, options?: TransitionNavigateOptions): Promise<void> =>
      transitionNavigate(to, { ...TRANSITION_OPTIONS, ...options }),
    [transitionNavigate],
  );

  const webNavigate = useCallback(
    (to: To | number, options?: TransitionNavigateOptions): Promise<void> => {
      if (typeof to === 'number') {
        plainNavigate(to);
      } else {
        plainNavigate(to, options);
      }
      return Promise.resolve();
    },
    [plainNavigate],
  );

  return isNativeApp ? nativeNavigate : webNavigate;
};

/**
 * Back navigation with the matching reverse transition. Use for header
 * back buttons so the leave animation mirrors the enter.
 */
export const useGoBack = (): (() => Promise<void>) => {
  const plainNavigate = useNavigate();
  const goBackWithTransition = useGoBackOriginal(pageTransitionConfig);

  const nativeGoBack = useCallback(
    (): Promise<void> => goBackWithTransition(TRANSITION_OPTIONS),
    [goBackWithTransition],
  );

  const webGoBack = useCallback((): Promise<void> => {
    plainNavigate(-1);
    return Promise.resolve();
  }, [plainNavigate]);

  return isNativeApp ? nativeGoBack : webGoBack;
};
